import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle } from 'lucide-react';

interface TooltipProps {
  text: string;
}

interface TooltipPos {
  top: number;
  left: number;
  above: boolean;
}

export function Tooltip({ text }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<TooltipPos>({ top: 0, left: 0, above: true });
  const btnRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const reposition = useCallback(() => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const tooltipWidth = 256;
    const tooltipHeight = 80;
    const spaceAbove = r.top;
    const above = spaceAbove >= tooltipHeight + 12;
    const top = above ? r.top - 8 : r.bottom + 8;
    const rawLeft = r.left + r.width / 2 - tooltipWidth / 2;
    const left = Math.max(8, Math.min(rawLeft, window.innerWidth - tooltipWidth - 8));
    setPos({ top, left, above });
  }, []);

  function handleClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (!open) reposition();
    setOpen(o => !o);
  }

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (
        btnRef.current?.contains(e.target as Node) ||
        tooltipRef.current?.contains(e.target as Node)
      ) return;
      setOpen(false);
    }
    function onScrollResize() { reposition(); }
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', onScrollResize, true);
    window.addEventListener('resize', onScrollResize);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', onScrollResize, true);
      window.removeEventListener('resize', onScrollResize);
    };
  }, [open, reposition]);

  return (
    <span className="inline-flex items-center ml-1.5">
      <button
        ref={btnRef}
        type="button"
        onClick={handleClick}
        className="text-gray-400 hover:text-blue-500 focus:outline-none"
        aria-label="More information"
      >
        <HelpCircle size={14} />
      </button>
      {open && createPortal(
        <div
          ref={tooltipRef}
          style={{
            position: 'fixed',
            top: pos.above ? pos.top : pos.top,
            left: pos.left,
            zIndex: 9999,
            width: 256,
            transform: pos.above ? 'translateY(-100%)' : 'none',
          }}
          className="bg-gray-900 text-white text-xs rounded-md p-2.5 shadow-lg"
        >
          {text}
          <div
            style={{ left: '50%', transform: 'translateX(-50%)' }}
            className={`absolute border-4 border-transparent ${
              pos.above
                ? 'top-full border-t-gray-900'
                : 'bottom-full border-b-gray-900'
            }`}
          />
        </div>,
        document.body
      )}
    </span>
  );
}
